import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent
} from 'react';
import { cardId, sortHand, type Card } from '@crew/shared';

const STORAGE_KEY = 'crew-online:hand-order';

/**
 * Keeps a player-chosen card order for their hand, the way you would rearrange
 * a physical hand. A fresh deal arrives sorted by colour and then low to high;
 * after that the order only changes when you drag, and it survives a refresh.
 */
export function useHandOrder(hand: readonly Card[]) {
  const [order, setOrder] = useState<string[]>(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });

  const ids = hand.map(cardId);
  const idKey = ids.join(',');
  const byId = new Map(hand.map((c) => [cardId(c), c]));

  const sortedIds = useCallback(() => sortHand(hand).map(cardId), [idKey]);

  useEffect(() => {
    setOrder((previous) => {
      const held = new Set(ids);
      const kept = previous.filter((id) => held.has(id));
      // Cards only ever arrive on a fresh deal, so that is when we re-sort.
      const dealt = kept.length < ids.length;
      const next = dealt ? sortHand(hand).map(cardId) : kept;
      if (next.length === previous.length && next.every((id, i) => id === previous[i])) {
        return previous;
      }
      persist(next);
      return next;
    });
    // idKey collapses the hand into a single comparable value.
  }, [idKey]);

  const move = useCallback((fromId: string, toId: string) => {
    setOrder((previous) => {
      const from = previous.indexOf(fromId);
      const to = previous.indexOf(toId);
      if (from < 0 || to < 0 || from === to) return previous;
      const next = previous.slice();
      next.splice(to, 0, next.splice(from, 1)[0] as string);
      persist(next);
      return next;
    });
  }, []);

  const sort = useCallback(() => {
    const next = sortedIds();
    persist(next);
    setOrder(next);
  }, [sortedIds]);

  const ordered = order.map((id) => byId.get(id)).filter((c): c is Card => c !== undefined);
  const missing = hand.filter((c) => !order.includes(cardId(c)));
  const cards = [...ordered, ...missing];
  const isSorted = cards.map(cardId).join(',') === sortHand(hand).map(cardId).join(',');

  return { cards, move, sort, isSorted };
}

function persist(order: string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
  } catch {
    /* private browsing */
  }
}

const DRAG_THRESHOLD = 7;
const LIFT_MIN = -70;
const LIFT_MAX = 40;

interface Slot {
  id: string;
  left: number;
  right: number;
  mid: number;
}

/**
 * Pointer-based hand reordering.
 *
 * Built to stay smooth on a phone: the moving card is transformed directly on
 * its DOM node instead of through React state, pointer events are coalesced to
 * one write per animation frame, and neighbour positions are measured once per
 * layout rather than on every event. Without those three, a 14 card hand
 * re-renders and re-measures on every pointermove, which is what made dragging
 * stutter on touch.
 */
export function useCardDrag(onMove: (fromId: string, toId: string) => void) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const gesture = useRef<{
    id: string;
    startX: number;
    startY: number;
    dragging: boolean;
    node: HTMLElement | null;
    pointerId: number;
  } | null>(null);
  const slots = useRef<Slot[]>([]);
  const frame = useRef(0);
  const latest = useRef({ x: 0, y: 0 });
  const moved = useRef(false);

  const measure = () => {
    slots.current = Array.from(document.querySelectorAll<HTMLElement>('[data-card-id]')).map(
      (el) => {
        const r = el.getBoundingClientRect();
        return { id: el.dataset.cardId as string, left: r.left, right: r.right, mid: r.left + r.width / 2 };
      }
    );
  };

  const onPointerDown = (id: string) => (e: ReactPointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const node = (e.currentTarget as HTMLElement) ?? null;
    gesture.current = {
      id,
      startX: e.clientX,
      startY: e.clientY,
      dragging: false,
      node,
      pointerId: e.pointerId
    };
    moved.current = false;
    measure();
  };

  useEffect(() => {
    const flush = () => {
      frame.current = 0;
      const g = gesture.current;
      if (!g?.dragging || !g.node) return;

      const dx = latest.current.x - g.startX;
      const dy = Math.max(LIFT_MIN, Math.min(LIFT_MAX, latest.current.y - g.startY));

      // Written straight to the node: no React render on the other 13 cards.
      g.node.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;

      const target = slots.current.find(
        (s) => s.id !== g.id && latest.current.x >= s.left && latest.current.x <= s.right
      );
      if (target) {
        const self = slots.current.find((s) => s.id === g.id);
        // Only swap once the pointer is past the neighbour's centre, so cards
        // do not oscillate when hovering a boundary.
        const movingRight = self ? target.mid > self.mid : true;
        const past = movingRight ? latest.current.x > target.mid : latest.current.x < target.mid;
        if (past) {
          onMove(g.id, target.id);
          g.startX = latest.current.x;
          g.node.style.transform = `translate3d(0px, ${dy}px, 0)`;
          requestAnimationFrame(measure);
        }
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      const g = gesture.current;
      if (!g) return;

      if (!g.dragging) {
        const dx = e.clientX - g.startX;
        const dy = e.clientY - g.startY;
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

        // On touch, a mostly-horizontal swipe means "scroll my hand", so bail
        // and let the browser pan. Only a vertical intent picks a card up.
        if (e.pointerType !== 'mouse' && Math.abs(dx) > Math.abs(dy)) {
          gesture.current = null;
          return;
        }

        g.dragging = true;
        moved.current = true;
        setDraggingId(g.id);
        // Captured only now: capturing on pointerdown would retarget the click
        // away from the card button and make cards unplayable.
        try {
          g.node?.setPointerCapture(g.pointerId);
        } catch {
          /* capture is a nicety, not a requirement */
        }
      }

      e.preventDefault();
      latest.current = { x: e.clientX, y: e.clientY };
      if (!frame.current) frame.current = requestAnimationFrame(flush);
    };

    const end = () => {
      const g = gesture.current;
      if (g?.node) {
        g.node.style.transform = '';
        try {
          if (g.dragging) g.node.releasePointerCapture(g.pointerId);
        } catch {
          /* already released by the browser */
        }
      }
      if (frame.current) cancelAnimationFrame(frame.current);
      frame.current = 0;
      gesture.current = null;
      setDraggingId(null);
    };

    window.addEventListener('pointermove', onPointerMove, { passive: false });
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      window.removeEventListener('resize', measure);
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [onMove]);

  /** True when the gesture that just ended was a drag, so the click is ignored. */
  const wasDrag = () => moved.current;

  return { draggingId, onPointerDown, wasDrag };
}
